<template>
  <div class="sidebar-height">

    <!-- Rail: icons only. A group opens the full sidebar with that group expanded. -->
    <ul class="rail-content" v-if="compact">
      <template v-for="(menuItem, index) in filteredMenu" :key="index">
        <li v-if="menuItem.type === 'header'" :key="'rh-' + index" class="rail-sep"></li>
        <li
          v-else-if="isGroup(menuItem)"
          :key="'rg-' + index"
          class="rail-item"
          :class="{ active: groupActive(menuItem), featured: menuItem.featured }"
          :title="$tr(menuItem.title)"
          @click="expandGroup(menuItem)"
        >
          <span class="nav-icon" v-if="groupIcon(menuItem)"><AppIcon :name="groupIcon(menuItem)" /></span>
          <i v-else class="fontello-icon"><span v-html="menuItem.icon"></span></i>
        </li>
        <li v-else-if="menuItem.type === 'link'" :key="'rl-' + index" class="rail-item" :title="$tr(menuItem.title)" @click="openLink(menuItem.href)">
          <span class="nav-icon"><AppIcon :name="menuItem.svgIcon" /></span>
        </li>
        <li v-else-if="menuItem.type === 'sso'" :key="'rs-' + index" class="rail-item" :title="$tr(menuItem.title)" @click="openSso(menuItem)">
          <span class="nav-icon"><AppIcon :name="menuItem.svgIcon" /></span>
        </li>
        <li v-else :key="'ri-' + index" class="rail-item" :class="{ active: isActive(menuItem.name) }" :title="menuItem.note ? $tr(menuItem.title) + ' — ' + $tr(menuItem.note) : $tr(menuItem.title)" @click="go(menuItem.name)">
          <span class="nav-icon" v-if="menuItem.svgIcon"><AppIcon :name="menuItem.svgIcon" /></span>
          <i v-else class="fontello-icon"><span v-html="menuItem.icon"></span></i>
        </li>
      </template>
    </ul>

    <!-- Navigation -->
    <ul class="sidebar-content" v-else>
      <template v-for="(menuItem, index) in filteredMenu" :key="index">

        <!-- Section header -->
        <li v-if="menuItem.type === 'header'" :key="'h-' + index" class="section-header">
          {{ $tr(menuItem.title) }}
        </li>

        <!-- Sales & Analytics collapsible -->
        <li v-else-if="menuItem.type === 'salesGroup'" :key="'sg-' + index" class="group-li">
          <div class="group-header" :class="{ open: salesAnalyticsOpen }" @click="salesAnalyticsOpen = !salesAnalyticsOpen">
            <div class="group-left">
              <span class="nav-icon"><AppIcon name="ic-chart" /></span>
              <span>{{ $tr(menuItem.title) }}</span>
            </div>
            <span class="chevron" :class="{ open: salesAnalyticsOpen }">&#8250;</span>
          </div>
          <div class="group-body" :class="{ open: salesAnalyticsOpen }">
            <template v-for="(sub, si) in menuItem.items" :key="si">
              <div v-if="sub.type === 'subheader'" :key="'ssh-' + si" class="sub-label">{{ $tr(sub.title) }}</div>
              <div v-else :key="'ss-' + si" class="sub-item" :class="{ active: isActive(sub.name) }" :title="$tr(sub.note)" @click="go(sub.name)">
                {{ $tr(sub.title) }}
              </div>
            </template>
          </div>
        </li>

        <!-- Deal Engine collapsible -->
        <li v-else-if="menuItem.type === 'dealEngineGroup'" :key="'de-' + index" class="group-li">
          <div class="group-header" :class="{ open: dealEngineOpen }" @click="dealEngineOpen = !dealEngineOpen">
            <div class="group-left">
              <span class="nav-icon"><AppIcon name="ic-auto" /></span>
              <span>{{ $tr(menuItem.title) }}</span>
            </div>
            <span class="chevron" :class="{ open: dealEngineOpen }">&#8250;</span>
          </div>
          <div class="group-body" :class="{ open: dealEngineOpen }">
            <div v-for="(sub, si) in menuItem.items" :key="'de-s-' + si" class="sub-item" :class="{ active: isActive(sub.name) }" :title="$tr(sub.note)" @click="go(sub.name)">
              {{ $tr(sub.title) }}
            </div>
          </div>
        </li>

        <!-- Customers collapsible -->
        <!-- CRM Analytics collapsible -->
        <li v-else-if="menuItem.type === 'crmAnalyticsGroup'" :key="'crm-' + index" class="group-li">
          <div class="group-header" :class="{ open: crmAnalyticsOpen }" @click="crmAnalyticsOpen = !crmAnalyticsOpen">
            <div class="group-left">
              <span class="nav-icon"><AppIcon name="ic-crm" /></span>
              <span>{{ $tr(menuItem.title) }}</span>
            </div>
            <span class="chevron" :class="{ open: crmAnalyticsOpen }">&#8250;</span>
          </div>
          <div class="group-body" :class="{ open: crmAnalyticsOpen }">
            <div v-for="(sub, si) in menuItem.items" :key="'crms-' + si" class="sub-item" :class="{ active: isActive(sub.name) }" :title="$tr(sub.note)" @click="go(sub.name)">
              {{ $tr(sub.title) }}
            </div>
          </div>
        </li>

        <!-- Nav item with sub-menu (inline expand) -->
        <!-- A featured group (featured: true in menu.js) stands out and opens by itself: the place the panel wants people to go. -->
        <li v-else-if="menuItem.subMenu" :key="'nm-' + index" class="group-li" :class="{ featured: menuItem.featured }">
          <div class="group-header" :class="{ open: expandedSubMenu === menuItem.name, active: isActive(menuItem.name) }" @click="toggleSubMenu(menuItem.name); menuItem.groupOnly || go(menuItem.name)">
            <div class="group-left">
              <span class="nav-icon" v-if="menuItem.svgIcon"><AppIcon :name="menuItem.svgIcon" /></span>
              <i v-else class="fontello-icon"><span v-html="menuItem.icon"></span></i>
              <span>{{ $tr(menuItem.title) }}</span>
              <span class="nav-badge" v-if="menuItem.badge">{{ menuItem.badge }}</span>
            </div>
            <span class="chevron" :class="{ open: expandedSubMenu === menuItem.name }">&#8250;</span>
          </div>
          <div class="group-body" :class="{ open: expandedSubMenu === menuItem.name }">
            <template v-for="(sub, si) in menuItem.subMenu" :key="si">
              <!-- Ayrac: tiklanmaz, sadece gruplari ayirir. The section of the open page is highlighted (Kubi's 8160b19). -->
              <div v-if="sub.divider" :key="'smd-' + si" class="sub-divider" :class="{ active: dividerActive(menuItem.subMenu, si) }">{{ $tr(sub.title) }}</div>
              <div
                v-else
                :key="'smi-' + si"
                class="sub-item"
                :class="{ active: subActive(sub) && !parentOpen(menuItem.subMenu, si), 'is-child': sub.child, 'is-open-parent': parentOpen(menuItem.subMenu, si) }"
                :title="$tr(sub.note)"
                @click.stop="go(sub.name, sub.query)"
              >
                {{ $tr(sub.title) }}
              </div>
            </template>
          </div>
        </li>

        <!-- External link -->
        <li v-else-if="menuItem.type === 'link'" :key="'lk-' + index" class="nav-item" :title="$tr(menuItem.note)" @click="openLink(menuItem.href)">
          <span class="nav-icon"><AppIcon :name="menuItem.svgIcon" /></span>
          {{ $tr(menuItem.title) }}
          <span style="margin-left:auto;font-size:var(--t-caption);opacity:0.38">&#8599;</span>
        </li>

        <!-- Another tool, opened in a new tab signed in as this person -->
        <li v-else-if="menuItem.type === 'sso'" :key="'so-' + index" class="nav-item" :title="$tr(menuItem.note)" @click="openSso(menuItem)">
          <span class="nav-icon"><AppIcon :name="menuItem.svgIcon" /></span>
          {{ $tr(menuItem.title) }}
          <span style="margin-left:auto;font-size:var(--t-caption);opacity:0.38">&#8599;</span>
        </li>

        <!-- Regular nav item -->
        <li v-else :key="'ni-' + index" class="nav-item" :class="{ active: isActive(menuItem.name) }" :title="$tr(menuItem.note)" @click="go(menuItem.name)">
          <span class="nav-icon" v-if="menuItem.svgIcon"><AppIcon :name="menuItem.svgIcon" /></span>
          <i v-else class="fontello-icon"><span v-html="menuItem.icon"></span></i>
          {{ $tr(menuItem.title) }}
        </li>

      </template>
    </ul>

    <!-- Bottom bar -->
    <div class="sidebar-bottom" :class="{ 'is-compact': compact }">
      <div class="nav-item logout" :title="compact ? $tr('Sign out') : ''" @click="logout()">
        <i class="flaticon-logout"></i>
        <span v-if="!compact">Logout</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import pages from "@/router/routes";
import { mainMenu, visibleMenu, GROUP_ICON } from "@/menu";
import { openAtlas } from "@/utils/atlasSso";

// Every page that actually exists. Built from the route table so the menu
// and the router can never disagree about what is reachable.
const ROUTE_NAMES = new Set(pages.map((r) => r.name));

const GROUP_FLAG = { salesGroup: "salesAnalyticsOpen", dealEngineGroup: "dealEngineOpen", crmAnalyticsGroup: "crmAnalyticsOpen" };

export default {
  name: "DashboardMenu",
  props: {
    // Rail mode: icons only, no labels; the sidebar decides.
    compact: { type: Boolean, default: false },
  },
  emits: ["expand"],
  data() {
    return {
      expandedSubMenu: "",
      salesAnalyticsOpen: false,
      dealEngineOpen: false,
      crmAnalyticsOpen: false,
    };
  },
  computed: {
    ...mapState({
      stateCommon: "common",
      stateAuth: "auth",
    }),
    /**
     * Route names this brand's backend does not serve.
     *
     * One codebase is built once per brand, so a page whose endpoint was never
     * deployed still shows up in the sidebar and fails the moment it is
     * clicked. VUE_APP_HIDDEN_PAGES lists those names, comma separated, and the
     * build for that brand drops them from the menu. Unset for La Ruota, which
     * has every page.
     */
    hiddenPages() {
      return (import.meta.env.VUE_APP_HIDDEN_PAGES || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    },
    filteredMenu() {
      // A group that is a workspace of its own (Inventory, owner 2026-11-02) opens from the top bar instead.
      return visibleMenu(this.stateAuth.current?.user?.utype, this.hiddenPages, this.$store.state.rbac.me.permissions, {
        allStores: this.$store.getters.isAllStores,
      }).filter((item) => !item.workspace);
    },
  },
  created() {
    this.openGroupForRoute();
    // Nothing opened for the page: the featured group opens, so its pages are in view from the start.
    if (!this.expandedSubMenu && !this.salesAnalyticsOpen && !this.dealEngineOpen && !this.crmAnalyticsOpen) {
      const featured = this.filteredMenu.find((i) => i.featured && i.subMenu && i.name);
      if (featured) this.expandedSubMenu = featured.name;
    }
  },
  watch: {
    "$route.name"() {
      this.openGroupForRoute();
    },
  },

  methods: {
    // Which item is highlighted is a fact about the URL, so read it from the
    // URL. Remembering it in the component was why a refresh used to clear the
    // highlight even though the page was still open.
    isActive(name) {
      return !!name && this.$route.name === name;
    },
    /**
     * A sub-item is active on its page; a tab link (query, e.g. Dough Management's tabs) only on its tab, and the
     * page's default tab (defaultTab) also when the address names none.
     */
    subActive(sub) {
      if (!this.isActive(sub.name)) return false;
      if (!sub.query) return true;
      return Object.keys(sub.query).every((k) => {
        const have = this.$route.query[k];
        return have === undefined || have === "" ? !!sub.defaultTab : String(have) === String(sub.query[k]);
      });
    },
    /** A page whose own tab links follow it (children of the same page) and one of them is open: the tab shows it. */
    parentOpen(list, at) {
      const sub = list[at];
      if (!sub || sub.query || sub.child) return false;
      for (let i = at + 1; i < list.length && list[i].child && list[i].name === sub.name; i++) if (this.subActive(list[i])) return true;
      return false;
    },
    /** A section heading is active while one of its pages (up to the next heading) is open. */
    dividerActive(list, at) {
      for (let i = at + 1; i < list.length && !list[i].divider; i++) if (this.subActive(list[i])) return true;
      return false;
    },
    go(name, query) {
      if (!name) return;
      // A menu entry whose page does not exist would navigate into an empty
      // shell with no explanation, so ignore the click instead. ROUTE_NAMES is
      // read from the route table itself, never guessed.
      if (!ROUTE_NAMES.has(name)) return;
      // The page already open: a tab link still moves to its tab, anything else stays.
      if (this.$route.name === name && (!query || Object.keys(query).every((k) => String(this.$route.query[k] || "") === String(query[k])))) return;
      // vue-router 3 rejects a navigation to the page you are already on;
      // that is not an error worth surfacing.
      this.$router.push(query ? { name, query } : { name }).catch(() => {});
    },
    // A link pasted into the address bar has to arrive with its own group
    // already open, or the page loads with no visible sign of where it sits.
    openGroupForRoute() {
      const routeName = this.$route.name;
      if (!routeName) return;
      const flagFor = {
        salesGroup: "salesAnalyticsOpen",
        dealEngineGroup: "dealEngineOpen",
        crmAnalyticsGroup: "crmAnalyticsOpen",
      };
      for (const item of mainMenu) {
        const children = item.items || item.subMenu;
        if (!children) continue;
        if (!children.some((c) => c.name === routeName)) continue;
        if (flagFor[item.type]) this[flagFor[item.type]] = true;
        else if (item.name) this.expandedSubMenu = item.name;
        return;
      }
    },
    toggleSubMenu(name) {
      this.expandedSubMenu = this.expandedSubMenu === name ? "" : name;
    },
    isGroup(item) {
      return !!(item.items || item.subMenu);
    },
    groupIcon(item) {
      return GROUP_ICON[item.type] || item.svgIcon || null;
    },
    groupActive(item) {
      const children = item.items || item.subMenu || [];
      return children.some((c) => c.name && c.name === this.$route.name) || this.isActive(item.name);
    },
    // In the rail a group cannot unfold, so open the full sidebar with it expanded.
    expandGroup(item) {
      if (GROUP_FLAG[item.type]) this[GROUP_FLAG[item.type]] = true;
      else if (item.name) this.expandedSubMenu = item.name;
      this.$emit("expand");
    },
    openLink(href) {
      window.open(href, "_blank");
    },
    async openSso(item) {
      const problem = await openAtlas(item.sso);
      if (problem) this.$store.commit("NOTIFICATION_MESSAGE", { status: -1, title: item.title, message: problem });
    },
    logout() {
      this.$store.dispatch("logoutService");
      this.$router.push("/login");
    },
  },
};
</script>

<style lang="css" scoped>
/* ── Rail (collapsed sidebar) ────────────────────────── */
.rail-content {
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}
.rail-sep {
  width: 28px;
  height: 1px;
  margin: 0.4rem 0;
  background: var(--sidebar-border);
}
.rail-item {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--r-md);
  color: var(--sidebar-text);
  cursor: pointer;
  transition: background var(--motion-fast) var(--ease-out), color var(--motion-fast) var(--ease-out);
}
.rail-item:hover {
  background: var(--bg-hover);
  color: var(--sidebar-text-hover);
}
.rail-item.active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
}
.rail-item .nav-icon,
.rail-item .fontello-icon {
  margin: 0;
  display: inline-flex;
}

.sub-divider {
  padding: 0.6rem 0 0.25rem 1.5rem;
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  opacity: 0.45;
  cursor: default;
  user-select: none;
  transition: opacity 0.15s ease, color 0.15s ease;
}
.sub-divider.active {
  opacity: 0.9;
  color: var(--brand-orange);
}
.sidebar-height {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}
.sidebar-content {
  padding: 0.5rem;
  margin: 0;
  list-style: none;
  overflow-y: auto;
  overscroll-behavior: contain;
  flex: 1;
}

/* ── Section header ──────────────────────────────────── */
.section-header {
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 1.25rem 0.85rem 0.4rem;
  pointer-events: none;
}

/* ── Regular nav item ────────────────────────────────── */
.nav-item {
  display: flex;
  align-items: center;
  padding: 0.55rem 0.85rem;
  color: var(--sidebar-text);
  font-size: var(--t-body);
  line-height: var(--t-body-lh);
  font-weight: 500;
  border-radius: 8px;
  gap: 0.65rem;
  cursor: pointer;
  position: relative;
  transition: var(--transition-fast, all 0.15s ease);
}
.nav-item:hover {
  background: var(--bg-hover);
  color: var(--sidebar-text-hover);
}
.nav-item.active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
  font-weight: 600;
}
.nav-item.active::before {
  content: '';
  position: absolute;
  left: -0.5rem;
  top: 20%;
  width: 3px;
  height: 60%;
  background: var(--sidebar-active-text);
  border-radius: 0 3px 3px 0;
}

/* ── SVG icon ────────────────────────────────────────── */
.nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  flex-shrink: 0;
}
.nav-icon svg {
  width: 1.15rem;
  height: 1.15rem;
}

/* ── Fontello icon in nav ────────────────────────────── */
.nav-item .fontello-icon,
.group-header .fontello-icon {
  font-size: var(--t-heading-sm);
  width: 1.25rem;
  text-align: center;
  opacity: 0.8;
  flex-shrink: 0;
}

/* ── Collapsible group ───────────────────────────────── */
.group-li {
  list-style: none;
}
.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.85rem;
  color: var(--sidebar-text);
  font-size: var(--t-body);
  line-height: var(--t-body-lh);
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: var(--transition-fast, all 0.15s ease);
}
.group-header:hover {
  background: var(--bg-hover);
  color: var(--sidebar-text-hover);
}
.group-header.open {
  color: var(--sidebar-text-hover);
}
.group-header.active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
}
.group-left {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}
.chevron {
  font-size: var(--t-heading-sm);
  opacity: 0.4;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.chevron.open {
  transform: rotate(90deg);
  opacity: 0.7;
}

/* ── Collapsible body ────────────────────────────────── */
.group-body {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.25s ease;
  padding-left: 0.5rem;
}
.group-body.open {
  max-height: 2000px;
}

.sub-label {
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 0.75rem 0.85rem 0.25rem;
  pointer-events: none;
}
.sub-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.85rem;
  color: var(--sidebar-text);
  font-size: var(--t-body);
  line-height: var(--t-body-lh);
  font-weight: 400;
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition-fast, all 0.15s ease);
}
.sub-item:hover {
  background: var(--bg-hover);
  color: var(--sidebar-text-hover);
}
.sub-item.active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
  font-weight: 600;
}
.sub-item.is-open-parent {
  color: var(--sidebar-active-text);
  font-weight: 600;
}
/* A page's own tabs (Dough Management): one step in, a little smaller. */
.sub-item.is-child {
  padding-left: 1.85rem;
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
}

/* ── Featured group: a branded card at the top ───────── */
.group-li.featured {
  margin: 0.25rem 0 0.6rem;
  padding: 0.3rem;
  background: linear-gradient(135deg, color-mix(in srgb, var(--brand-orange) 13%, transparent), color-mix(in srgb, var(--brand-orange) 4%, transparent));
  border: 1px solid color-mix(in srgb, var(--brand-orange) 30%, transparent);
  border-radius: 12px;
}
.group-li.featured .group-header {
  font-weight: 650;
  color: var(--text-primary);
}
.group-li.featured .group-header:hover {
  background: color-mix(in srgb, var(--brand-orange) 10%, transparent);
}
.group-li.featured .group-header .nav-icon {
  width: 1.6rem;
  height: 1.6rem;
  color: #fff;
  background: var(--brand-orange);
  border-radius: 7px;
  transition: box-shadow var(--motion-fast, 0.15s) var(--ease-out, ease);
}
.group-li.featured .group-header:hover .nav-icon {
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--brand-orange) 22%, transparent);
}
.group-li.featured .group-header .nav-icon svg {
  width: 1rem;
  height: 1rem;
}
.nav-badge {
  padding: 1px 7px;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--brand-orange);
  background: color-mix(in srgb, var(--brand-orange) 14%, transparent);
  border-radius: 999px;
}
.group-li.featured .group-body {
  margin: 0.1rem 0 0.2rem 1.35rem;
  padding-left: 0.4rem;
  border-left: 2px solid color-mix(in srgb, var(--brand-orange) 35%, transparent);
}
.group-li.featured .sub-item.active {
  color: var(--brand-orange);
  background: color-mix(in srgb, var(--brand-orange) 12%, transparent);
}
.rail-item.featured {
  color: var(--brand-orange);
  background: color-mix(in srgb, var(--brand-orange) 10%, transparent);
}
@media (prefers-reduced-motion: reduce) {
  .group-li.featured .group-header .nav-icon {
    transition: none;
  }
}

/* ── Bottom bar ──────────────────────────────────────── */
.sidebar-bottom {
  padding: 0.75rem;
  flex-shrink: 0;
  border-top: 1px solid var(--sidebar-border);
}
.sidebar-bottom.is-compact {
  padding: 0.5rem 0;
  display: flex;
  justify-content: center;
}
.sidebar-bottom.is-compact .logout {
  width: 40px;
  height: 40px;
  justify-content: center;
  padding: 0;
  gap: 0;
}
.logout {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--t-body);
  line-height: var(--t-body-lh);
  color: var(--sidebar-text);
  cursor: pointer;
  padding: 0.45rem 0.75rem;
  border-radius: 8px;
  transition: var(--transition-fast, all 0.15s ease);
}
.logout:hover {
  color: var(--danger, #ef4444);
  background: var(--danger-bg, rgba(239, 68, 68, 0.1));
}
.flaticon-logout {
  vertical-align: middle;
  font-size: var(--t-heading-sm);
}
</style>
